import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, ChevronLeft, ChevronRight, Upload, X, Check, AlertCircle, FileImage, FileVideo, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "mobbing", label: "Mobbing", description: "Jemand ärgert oder verletzt mich immer wieder", color: "#FFC4D6" },
  { id: "cybermobbing", label: "Cybermobbing", description: "Mobbing im Internet oder über Handy", color: "#BDE0FE" },
  { id: "identitaetsdiebstahl", label: "Identitätsdiebstahl", description: "Jemand gibt sich als ich aus", color: "#A0E8AF" },
  { id: "andere", label: "Etwas anderes", description: "Ich bin mir nicht sicher was es ist", color: "#F6E05E" },
];

export default function ReportForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form data
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [wantsContact, setWantsContact] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: "", email: "", phone: "" });
  
  // Captcha
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  
  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  const loadCaptcha = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/captcha`);
      setCaptcha(res.data);
      setCaptchaAnswer("");
      setCaptchaError("");
    } catch (e) {
      console.error("Failed to load captcha", e);
      toast.error("Konnte Sicherheitsfrage nicht laden");
    }
  }, []);

  useEffect(() => {
    if (step === 5) {
      loadCaptcha();
    }
  }, [step, loadCaptcha]);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(file => {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß (max. 50MB)`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const canProceed = () => {
    switch (step) {
      case 1: return category !== "";
      case 2: return description.trim().length >= 20;
      case 3: return true; // Files are optional
      case 4: return !wantsContact || (contactInfo.email.trim() !== "");
      case 5: return captchaAnswer !== "";
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!captcha) {
      toast.error("Bitte warte auf die Sicherheitsfrage");
      return;
    }

    setIsSubmitting(true);
    setCaptchaError("");

    try {
      // First verify captcha
      const verifyRes = await axios.post(`${API}/captcha/verify`, {
        captcha_id: captcha.captcha_id,
        answer: parseInt(captchaAnswer)
      });

      if (!verifyRes.data.valid) {
        setCaptchaError(verifyRes.data.message);
        loadCaptcha();
        setIsSubmitting(false);
        return;
      }

      // Create report
      const reportData = {
        description,
        category,
        captcha_id: captcha.captcha_id,
        captcha_answer: parseInt(captchaAnswer),
        contact_info: wantsContact ? contactInfo : null
      };

      const reportRes = await axios.post(`${API}/reports`, reportData);
      const reportId = reportRes.data.id;

      // Upload files if any
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        try {
          await axios.post(`${API}/reports/${reportId}/files`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        } catch (e) {
          console.error("Failed to upload file", e);
          toast.error(`Konnte ${file.name} nicht hochladen`);
        }
      }

      // Success!
      navigate("/erfolg");
    } catch (e) {
      console.error("Submit error", e);
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
        if (e.response.data.detail.includes("Captcha")) {
          loadCaptcha();
        }
      } else {
        toast.error("Ein Fehler ist aufgetreten. Bitte versuche es nochmal.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen gradient-hero">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-6">
        <nav className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="mobbing-melden.de Logo" 
              className="h-14 w-auto"
            />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-[#718096] hover:text-[#2D3748] rounded-full">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Zurück
            </Button>
          </Link>
        </nav>
      </header>

      {/* Progress */}
      <div className="px-6 md:px-12 lg:px-24 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#718096]">Schritt {step} von {totalSteps}</span>
            <span className="text-sm text-[#718096]">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-3 bg-gray-100" />
        </div>
      </div>

      {/* Form Container */}
      <main className="px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="card-base" data-testid="report-form-container">
            
            {/* Step 1: Category */}
            {step === 1 && (
              <div className="animate-fade-in-up">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                  Was ist passiert?
                </h2>
                <p className="text-[#718096] mb-8">Wähle aus, was am besten passt.</p>
                
                <div className="grid gap-4">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setCategory(cat.id)}
                      className={`p-6 rounded-2xl border-2 text-left transition-all hover:-translate-y-1 ${
                        category === cat.id 
                          ? `border-[${cat.color}] bg-[${cat.color}]/10 ring-4 ring-[${cat.color}]/20` 
                          : 'border-gray-100 hover:border-gray-200'
                      }`}
                      style={category === cat.id ? { borderColor: cat.color, backgroundColor: `${cat.color}20` } : {}}
                      data-testid={`category-${cat.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${cat.color}40` }}
                        >
                          {category === cat.id ? (
                            <Check className="w-6 h-6" style={{ color: cat.color.replace('FF', '99') }} />
                          ) : (
                            <div className="w-6 h-6 rounded-full border-2 border-gray-200" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-bold text-[#2D3748] text-lg">{cat.label}</h3>
                          <p className="text-[#718096] text-sm">{cat.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Description */}
            {step === 2 && (
              <div className="animate-fade-in-up">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                  Erzähl uns mehr
                </h2>
                <p className="text-[#718096] mb-8">
                  Beschreibe in deinen eigenen Worten, was passiert ist. 
                  Nimm dir Zeit - jedes Detail kann helfen.
                </p>
                
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Was ist passiert? Wer war beteiligt? Wann und wo ist es passiert?..."
                  className="input-base w-full h-48 resize-none"
                  data-testid="description-textarea"
                />
                
                <div className="flex items-center justify-between mt-4">
                  <span className={`text-sm ${description.length < 20 ? 'text-[#718096]' : 'text-[#68D391]'}`}>
                    {description.length} Zeichen {description.length < 20 && "(mindestens 20)"}
                  </span>
                </div>
              </div>
            )}

            {/* Step 3: Files */}
            {step === 3 && (
              <div className="animate-fade-in-up">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                  Hast du Beweise?
                </h2>
                <p className="text-[#718096] mb-8">
                  Screenshots, Fotos oder Videos helfen uns, deinen Fall besser zu verstehen. 
                  Du kannst diesen Schritt auch überspringen.
                </p>
                
                {/* Upload Zone */}
                <label 
                  className="block border-2 border-dashed border-[#BDE0FE] rounded-2xl p-8 text-center cursor-pointer hover:bg-[#BDE0FE]/10 transition-colors"
                  data-testid="file-upload-zone"
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <div className="w-16 h-16 bg-[#BDE0FE]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-[#1A365D]" />
                  </div>
                  <p className="text-[#2D3748] font-medium mb-1">Klicke hier oder ziehe Dateien hierher</p>
                  <p className="text-sm text-[#718096]">Bilder oder Videos (max. 50MB pro Datei)</p>
                </label>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-6 space-y-3">
                    <p className="text-sm text-[#718096]">{files.length} Datei(en) ausgewählt:</p>
                    {files.map((file, index) => (
                      <div 
                        key={index} 
                        className="flex items-center justify-between bg-gray-50 rounded-xl p-4"
                        data-testid={`uploaded-file-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          {file.type.startsWith('image/') ? (
                            <FileImage className="w-5 h-5 text-[#BDE0FE]" />
                          ) : (
                            <FileVideo className="w-5 h-5 text-[#FFC4D6]" />
                          )}
                          <span className="text-[#2D3748] truncate max-w-xs">{file.name}</span>
                          <span className="text-xs text-[#A0AEC0]">
                            ({(file.size / 1024 / 1024).toFixed(1)} MB)
                          </span>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                          data-testid={`remove-file-${index}`}
                        >
                          <X className="w-4 h-4 text-[#718096]" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Contact Info */}
            {step === 4 && (
              <div className="animate-fade-in-up">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                  Möchtest du eine Antwort?
                </h2>
                <p className="text-[#718096] mb-8">
                  Wenn du möchtest, dass wir dir antworten können, gib uns deine Kontaktdaten.
                  Das ist komplett freiwillig!
                </p>
                
                <div className="space-y-4">
                  <button
                    onClick={() => setWantsContact(false)}
                    className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                      !wantsContact 
                        ? 'border-[#A0E8AF] bg-[#A0E8AF]/10 ring-4 ring-[#A0E8AF]/20' 
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                    data-testid="anonymous-option"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        !wantsContact ? 'border-[#A0E8AF] bg-[#A0E8AF]' : 'border-gray-200'
                      }`}>
                        {!wantsContact && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#2D3748]">Anonym bleiben</h3>
                        <p className="text-sm text-[#718096]">Ich möchte keine Kontaktdaten angeben</p>
                      </div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => setWantsContact(true)}
                    className={`w-full p-6 rounded-2xl border-2 text-left transition-all ${
                      wantsContact 
                        ? 'border-[#BDE0FE] bg-[#BDE0FE]/10 ring-4 ring-[#BDE0FE]/20' 
                        : 'border-gray-100 hover:border-gray-200'
                    }`}
                    data-testid="contact-option"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        wantsContact ? 'border-[#BDE0FE] bg-[#BDE0FE]' : 'border-gray-200'
                      }`}>
                        {wantsContact && <Check className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <h3 className="font-bold text-[#2D3748]">Kontaktdaten angeben</h3>
                        <p className="text-sm text-[#718096]">Ich möchte eine Antwort bekommen können</p>
                      </div>
                    </div>
                  </button>
                </div>

                {wantsContact && (
                  <div className="mt-6 space-y-4 animate-fade-in-up">
                    <div>
                      <label className="block text-sm font-medium text-[#2D3748] mb-2">Name (optional)</label>
                      <input
                        type="text"
                        value={contactInfo.name}
                        onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Dein Name oder Spitzname"
                        className="input-base w-full"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2D3748] mb-2">E-Mail *</label>
                      <input
                        type="email"
                        value={contactInfo.email}
                        onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="deine@email.de"
                        className="input-base w-full"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2D3748] mb-2">Telefon (optional)</label>
                      <input
                        type="tel"
                        value={contactInfo.phone}
                        onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="0123 456789"
                        className="input-base w-full"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Captcha */}
            {step === 5 && (
              <div className="animate-fade-in-up">
                <h2 className="text-2xl md:text-3xl font-bold text-[#2D3748] mb-2" style={{ fontFamily: 'Nunito' }}>
                  Fast geschafft!
                </h2>
                <p className="text-[#718096] mb-8">
                  Beantworte diese kleine Frage, damit wir wissen dass du kein Roboter bist.
                </p>
                
                {captcha ? (
                  <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-2xl p-8 text-center">
                    <p className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
                      {captcha.question}
                    </p>
                    <input
                      type="number"
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      placeholder="Deine Antwort"
                      className="input-base w-32 text-center text-2xl mx-auto"
                      data-testid="captcha-answer-input"
                    />
                    {captchaError && (
                      <div className="flex items-center justify-center gap-2 mt-4 text-[#FC8181]">
                        <AlertCircle className="w-4 h-4" />
                        <span>{captchaError}</span>
                      </div>
                    )}
                    <button 
                      onClick={loadCaptcha}
                      className="mt-4 text-sm text-[#718096] hover:text-[#2D3748] underline"
                      data-testid="new-captcha-button"
                    >
                      Neue Frage
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-[#A0E8AF] mx-auto" />
                    <p className="text-[#718096] mt-2">Lade Sicherheitsfrage...</p>
                  </div>
                )}
                
                {/* Summary */}
                <div className="mt-8 p-4 bg-gray-50 rounded-xl">
                  <h4 className="font-medium text-[#2D3748] mb-2">Zusammenfassung:</h4>
                  <ul className="text-sm text-[#718096] space-y-1">
                    <li>• Kategorie: {CATEGORIES.find(c => c.id === category)?.label}</li>
                    <li>• Beschreibung: {description.length} Zeichen</li>
                    <li>• Dateien: {files.length} hochgeladen</li>
                    <li>• Kontakt: {wantsContact ? "Angegeben" : "Anonym"}</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
              {step > 1 ? (
                <Button
                  variant="ghost"
                  onClick={() => setStep(step - 1)}
                  className="text-[#718096] hover:text-[#2D3748] rounded-full"
                  data-testid="back-button"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Zurück
                </Button>
              ) : (
                <div />
              )}
              
              {step < totalSteps ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="btn-primary"
                  data-testid="next-button"
                >
                  Weiter
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!canProceed() || isSubmitting}
                  className="btn-primary"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wird gesendet...
                    </>
                  ) : (
                    <>
                      Absenden
                      <Check className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
